import { IconButton } from '@plato/ui/candidates/icon-button';
import { cn } from '@plato/ui/util/classes';
import {
    MessageScroller as MessageScrollerPrimitive,
    useMessageScroller,
    useMessageScrollerScrollable,
    useMessageScrollerVisibility,
} from '@shadcn/react/message-scroller';
import { ArrowDownIcon } from 'lucide-react';
import type { ComponentProps, ReactElement } from 'react';

export { useMessageScroller, useMessageScrollerScrollable, useMessageScrollerVisibility };

export type MessageScrollerProviderProps = ComponentProps<typeof MessageScrollerPrimitive.Provider>;

export function MessageScrollerProvider({
    autoScroll = true,
    ...props
}: MessageScrollerProviderProps): ReactElement {
    return <MessageScrollerPrimitive.Provider autoScroll={autoScroll} {...props} />;
}

export type MessageScrollerProps = ComponentProps<typeof MessageScrollerPrimitive.Root>;

export function MessageScroller({ className, ...props }: MessageScrollerProps): ReactElement {
    return (
        <MessageScrollerPrimitive.Root
            data-slot="message-scroller"
            className={cn(
                'group/message-scroller relative flex size-full min-h-0 flex-col overflow-hidden',
                className,
            )}
            {...props}
        />
    );
}

export type MessageScrollerViewportProps = ComponentProps<typeof MessageScrollerPrimitive.Viewport>;

export function MessageScrollerViewport({ className, ...props }: MessageScrollerViewportProps): ReactElement {
    return (
        <MessageScrollerPrimitive.Viewport
            data-slot="message-scroller-viewport"
            className={cn(
                'size-full min-h-0 min-w-0 overflow-y-auto overscroll-contain contain-content',
                className,
            )}
            {...props}
        />
    );
}

export type MessageScrollerContentProps = ComponentProps<typeof MessageScrollerPrimitive.Content>;

export function MessageScrollerContent({ className, ...props }: MessageScrollerContentProps): ReactElement {
    return (
        <MessageScrollerPrimitive.Content
            data-slot="message-scroller-content"
            className={cn('flex h-max min-h-full flex-col gap-relaxed p-default', className)}
            {...props}
        />
    );
}

export type MessageScrollerItemProps = ComponentProps<typeof MessageScrollerPrimitive.Item>;

export function MessageScrollerItem({
    className,
    scrollAnchor = false,
    ...props
}: MessageScrollerItemProps): ReactElement {
    return (
        <MessageScrollerPrimitive.Item
            data-slot="message-scroller-item"
            scrollAnchor={scrollAnchor}
            className={cn(
                'min-w-0 shrink-0 [contain-intrinsic-size:auto_10rem] [content-visibility:auto]',
                className,
            )}
            {...props}
        />
    );
}

export type MessageScrollerButtonProps = ComponentProps<typeof MessageScrollerPrimitive.Button> & {
    label?: string;
};

export function MessageScrollerButton({
    className,
    children,
    direction = 'end',
    label,
    render,
    ...props
}: MessageScrollerButtonProps): ReactElement | null {
    return (
        <MessageScrollerPrimitive.Button
            data-slot="message-scroller-button"
            data-direction={direction}
            direction={direction}
            className={cn(
                'absolute left-1/2 -translate-x-1/2',
                'rounded-full border border-line-weak bg-container shadow-md',
                'transition duration-state ease-state',
                'data-[active=false]:pointer-events-none data-[active=false]:scale-95 data-[active=false]:opacity-0',
                'data-[direction=end]:bottom-cozy data-[direction=start]:top-cozy',
                'data-[direction=start]:*:[svg]:rotate-180',
                className,
            )}
            render={
                render ?? (
                    <IconButton
                        label={label ?? (direction === 'end' ? 'Scroll to bottom' : 'Scroll to start')}
                    />
                )
            }
            {...props}
        >
            {children ?? <ArrowDownIcon />}
        </MessageScrollerPrimitive.Button>
    );
}
